import { Fragment, ReactNode, type JSX } from 'react';
import { Col, Row } from 'react-bootstrap';

import { TopMovieCard } from '../MovieCard/TopMovieCard';

import { UnfinishedMovie } from '@/api/types/home.types';
import { Movie, MoviesCinema } from '@/api/types/movie.types';

import { MovieCard, MovieCardProps } from '@/components/MovieCard';
import styles from '@/components/MovieList/MovieList.module.scss';
import { ResultNotFound } from '@/components/ResultNotFound';
import { SectionHeading } from '@/components/SectionHeading';
import { TruncatedText } from '@/components/TruncatedText';
import { UnfinishedMovieCard } from '@/components/UnfinishedMovieCard/UnfinishedMovieCard';

export enum MovieCardTypeEnum {
  MOVIE = 'movie',
  UNFINISHED_MOVIE = 'unfinished-movie',
  MOVIE_THEATER = 'movie-theater',
}

// =================================================================

export interface Breakpoints {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

interface CommonProps
  extends Pick<MovieCardProps, 'isFavorite' | 'isRemovable' | 'onToggled' | 'onRemoved'> {
  title?: string;
  description?: string;
  breakpoints?: Breakpoints;
  emptyOptions?: {
    title: string;
    description: string;
    icon: JSX.Element;
  };
  rightTopPanel?: ReactNode;
  is_new?: number;
}

type MovieListProps = CommonProps &
  (
    | {
        type?: 'movie';
        movies: Movie[];
      }
    | {
        type?: 'unfinished-movie';
        movies: UnfinishedMovie[];
      }
  );

// =================================================================

const defaultBreakpoints = {
  xs: 2,
  sm: 3,
  md: 4,
  lg: 5,
  xl: 6,
};

// =================================================================

export const MovieList = (props: MovieListProps) => {
  const {
    title,
    movies,
    description,
    emptyOptions,
    type = MovieCardTypeEnum.MOVIE,
    breakpoints = defaultBreakpoints,
    rightTopPanel,
    is_new,
    ...rest
  } = props;

  const getRenderCard: Partial<
    Record<MovieCardTypeEnum, (movie: Movie | UnfinishedMovie | MoviesCinema) => JSX.Element>
  > = {
    [MovieCardTypeEnum.MOVIE]: (movie: Movie | UnfinishedMovie | MoviesCinema) => (
      <MovieCard movie={movie as Movie} {...rest} />
    ),
    [MovieCardTypeEnum.UNFINISHED_MOVIE]: (movie: Movie | UnfinishedMovie | MoviesCinema) => (
      <UnfinishedMovieCard deepRemove movie={movie as UnfinishedMovie} {...rest} />
    ),
  };

  return (
    <Fragment>
      {(title || description || rightTopPanel) && (
        <div className={styles.topPanel}>
          <div>
            {title && <SectionHeading as="h1">{title}</SectionHeading>}
            {description && (
              <TruncatedText lines={2} className={styles.description}>
                {description}
              </TruncatedText>
            )}
          </div>
          {rightTopPanel}
        </div>
      )}
      {movies.length > 0 && (
        <Row as="ul" {...breakpoints} className="mb-3">
          {movies.map((movie, index) => (
            <Col key={movie?.id || index} as="li" className="mb-4">
              {is_new === 1 ? (
                <TopMovieCard movie={movie as Movie} ratingIndex={index + 1} />
              ) : (
                <Fragment>{getRenderCard[type]?.(movie)}</Fragment>
              )}
            </Col>
          ))}
        </Row>
      )}
      {movies.length === 0 && <ResultNotFound {...emptyOptions} />}
    </Fragment>
  );
};
